import { Capacitor } from '@capacitor/core'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { v4 as uuid4 } from 'uuid'

import { loginSupport } from '@/api/auth'
import { authApi } from '@/api/client'
import mqtt from '@/sync/mqtt'

async function getDeviceInfo() {
  const platform = Capacitor.getPlatform()
  if (platform === 'android') {
    const { Device } = await import('@capacitor/device')
    const { App } = await import('@capacitor/app')
    const deviceInfo = await Device.getInfo()
    const deviceId = await Device.getId().then(res => res.identifier)
    let appVersion = '1.0.0'
    let appBuild = '1'
    try {
      const appInfo = await App.getInfo()
      appVersion = appInfo.version
      appBuild = appInfo.build
    } catch {
      // web or dev mode
    }
    return {
      id: deviceId,
      platform: 'android' as const,
      model: deviceInfo.model,
      os: 'android' as const,
      osVersion: deviceInfo.osVersion,
      appVersion,
      appBuild,
      webviewVersion: deviceInfo.webViewVersion
    }
  }
  return {
    id: localStorage.getItem('kds-device-id') || (() => { const id = uuid4(); localStorage.setItem('kds-device-id', id); return id })(),
    platform: 'android' as const,
    model: 'web-kds',
    os: 'android' as const,
    osVersion: '1',
    appVersion: '1.0.0',
    appBuild: '1',
    webviewVersion: ''
  }
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const router = useRouter()

    const accessToken = ref<string>()
    const locationId = ref<string>()
    const organizationId = ref<string>()

    const isAuthenticated = computed(() => !!accessToken.value)

    async function login(data: { pin: string }) {
      const deviceInfo = await getDeviceInfo()
      const response = await authApi.login({
        loginRequest: {
          ...data,
          deviceInfo
        }
      })
      accessToken.value = response.accessToken
      if (response.locationId) {
        locationId.value = response.locationId
        mqtt.subscribe(locationId.value)
      }
    }

    function setAccessToken(token: string) {
      accessToken.value = token
    }

    async function redirectLogin({ accessToken }: { accessToken: string }) {
      if (!accessToken) return false

      setAccessToken(accessToken)
      return true
    }

    async function supportLogin({
      accessToken,
      selectedOrganizationId,
      selectedLocationId
    }: {
      accessToken: string
      selectedOrganizationId: string
      selectedLocationId: string
    }) {
      const supportUserIsValid = await loginSupport(accessToken)
      if (supportUserIsValid) {
        setAccessToken(accessToken)
        organizationId.value = selectedOrganizationId
        locationId.value = selectedLocationId
        return true
      }
      return false
    }

    async function logout() {
      accessToken.value = undefined
      locationId.value = undefined
      router.push({ name: 'login' })
    }

    return {
      accessToken,
      locationId,
      location,
      organizationId,
      isAuthenticated,

      login,
      setAccessToken,
      redirectLogin,
      supportLogin,
      logout
    }
  },
  { persist: true }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
